import { useMutation, useQueryClient } from '@tanstack/react-query';

import { changePassword } from '@/services/auth';
import { updateAccountInfo, updateUserInfo, validatePassword } from '@/services/profile';

import type { TUpdateAccountPayload, TUpdateUserPayload, TValidatePassword } from '@/types/profile';

import { profileKeys } from '@/queries/profile';

/**
 * W12 `updateUserInfo`: saves the signed-in user's own details (name, email, password at sign-up,
 * "profile filled" flag).
 * What: resolves to the saved user (`response.data.data`).
 * Cancels before: nothing (no user query exists; the user lives in `AuthProvider`).
 * Marks old after: all profile queries (`profileKeys.all`).
 * Constraint: this hook does NOT call `AuthProvider.updateProfile`. The caller does, with the
 * returned user, at the point of its own flow where it did before (for example after the wizard
 * step moved on), because the order matters there.
 * Used by: Change email, sign-up steps `PersonalInformation` and `TermsAndConditions`.
 * Errors: not handled here; the caller shows its own message and logs.
 */
export const useUpdateUserInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TUpdateUserPayload) =>
      updateUserInfo(payload).then((response) => response.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries(profileKeys.all);
    },
  });
};

/**
 * W13 `updateAccountInfo`: saves the business (account) details: business info, address, terms
 * version.
 * Cancels before: `profileKeys.accountInfo()`, so an older answer cannot overwrite the saved one.
 * Marks old after: `profileKeys.accountInfo()` (Settings > Account Details refetches at once if it
 * is open, or on its next visit). Marked old, not written from the PATCH answer: the answer may
 * not carry the whole account, so the next read asks the server. `PartnerRoleProvider` reads only
 * the partner role, which this save does not change, so it needs no refresh.
 * Used by: Change business address, sign-up steps `BusinessInformation`, `StationLocation`,
 * `TermsAndConditions`.
 * Errors: not handled here; the caller shows its own message and logs.
 */
export const useUpdateAccountInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TUpdateAccountPayload) =>
      updateAccountInfo(payload).then((response) => response.data.data),
    onMutate: () => queryClient.cancelQueries(profileKeys.accountInfo()),
    onSuccess: () => {
      queryClient.invalidateQueries(profileKeys.accountInfo());
    },
  });
};

/**
 * W14 `validatePassword`: checks the current password before a sensitive change (Change email).
 * Plain mutation: it only checks, so nothing is cancelled or invalidated.
 * Errors: not handled here; the caller reads the error code (`SVC_006` = wrong password).
 */
export const useValidatePassword = () =>
  useMutation({
    mutationFn: (payload: Parameters<TValidatePassword>[0]) => validatePassword(payload),
  });

/**
 * W15 `changePassword`: sets a new password (Settings > Change password, and Forgot password).
 * Plain mutation: no screen shows the password, so nothing is cancelled or invalidated.
 * Errors: not handled here; the caller maps error codes `ID_003` and `ID_005` to messages.
 */
export const useChangePassword = () =>
  useMutation({
    mutationFn: (payload: Parameters<typeof changePassword>[0]) => changePassword(payload),
  });
